
import { useState } from "react";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useUserInvitations } from "@/hooks/useUserInvitations";
import { UserRole, User } from "@/lib/types";
import { Input } from "@/components/ui/input";
import { Search, UserPlus, Users, Shield, Bug } from "lucide-react";
import { SUPABASE_URL } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { UsersTable } from "./UsersTable";
import { AddRoleDialog } from "./AddRoleDialog";
import { TestNotificationDialog } from "./TestNotificationDialog";
import { DeleteUserDialog } from "./DeleteUserDialog";
import { InviteUserDialog } from "./InviteUserDialog";
import { InvitedUsersTable } from "./InvitedUsersTable";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

type UserRoleFilter = "all" | UserRole;

export function UserManagement() {
  const { 
    useAllUsersQuery, 
    useAddRoleMutation, 
    useRemoveRoleMutation,
    useCreateTestNotificationMutation,
    useDeleteUserMutation
  } = useUserRoles();
  
  const {
    useInvitedUsersQuery,
    useResendInvitationMutation,
    useDeleteInvitationMutation
  } = useUserInvitations();
  
  const { data: users = [], isLoading: isLoadingUsers } = useAllUsersQuery();
  const { data: invitedUsers = [], isLoading: isLoadingInvitations } = useInvitedUsersQuery();
  
  const addRole = useAddRoleMutation();
  const removeRole = useRemoveRoleMutation();
  const createTestNotification = useCreateTestNotificationMutation();
  const deleteUser = useDeleteUserMutation();
  const resendInvitation = useResendInvitationMutation();
  const deleteInvitation = useDeleteInvitationMutation();
  
  const { getAccessToken } = useAuth();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isAddRoleDialogOpen, setIsAddRoleDialogOpen] = useState(false);
  const [isTestNotificationDialogOpen, setIsTestNotificationDialogOpen] = useState(false);
  const [isDeleteUserDialogOpen, setIsDeleteUserDialogOpen] = useState(false);
  const [isInviteUserDialogOpen, setIsInviteUserDialogOpen] = useState(false);
  const [sendingState, setSendingState] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [activeTab, setActiveTab] = useState<"users" | "invitations">("users");
  const [roleFilter, setRoleFilter] = useState<UserRoleFilter>("USER");
  
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.name?.toLowerCase() || "").includes(searchQuery.toLowerCase());
    
    if (!matchesSearch) return false;
    
    if (roleFilter === "all") {
      return true;
    }
    
    return Array.isArray(user.roles) && user.roles.includes(roleFilter);
  });

  const filteredInvitations = invitedUsers.filter(user => 
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (user.name?.toLowerCase() || "").includes(searchQuery.toLowerCase())
  );
  
  const pendingInvitationsCount = invitedUsers.filter(user => 
    user.status === 'pending'
  ).length;

  // Check if a user has the admin role
  const hasAdminRole = (user: User): boolean => {
    return Array.isArray(user.roles) && user.roles.includes("ADMIN");
  };

  // Count users with admin role
  const countAdmins = (): number => {
    return users.filter(user => hasAdminRole(user)).length;
  };

  // Check if this is the last admin user
  const isLastAdminUser = (userId: string): boolean => {
    const adminCount = countAdmins();
    const userIsAdmin = users.find(u => u.id === userId && hasAdminRole(u));
    return adminCount === 1 && !!userIsAdmin;
  };

  const handleAddRole = async (role: UserRole) => {
    if (selectedUser) {
      await addRole.mutateAsync({ 
        userId: selectedUser.id, 
        role
      });
      setIsAddRoleDialogOpen(false);
    }
  };

  const handleRemoveRole = async (userId: string, role: UserRole) => {
    // Prevent removing the last admin role
    if (role === "ADMIN" && isLastAdminUser(userId)) {
      toast.error("Cannot remove the last admin role from the system");
      return;
    }
    
    await removeRole.mutateAsync({ userId, role });
  };

  const handleOpenAddRoleDialog = (user: User) => {
    setSelectedUser(user);
    setIsAddRoleDialogOpen(true);
  };

  const handleOpenTestNotificationDialog = (user: User) => {
    setSelectedUser(user);
    setIsTestNotificationDialogOpen(true);
  };

  const handleSendTestNotification = async ({ 
    notificationType, 
    notificationTitle, 
    notificationMessage, 
    sendEmail 
  }: {
    notificationType: "reminder" | "system" | "info";
    notificationTitle: string;
    notificationMessage: string;
    sendEmail: boolean;
  }) => {
    if (!selectedUser) return;
    
    setSendingState("sending");
    
    if (sendEmail) {
      await handleSendTestEmail(notificationTitle, notificationMessage);
    } else {
      await handleSendTestInAppNotification(notificationType, notificationTitle, notificationMessage);
    }
  };

  const handleSendTestEmail = async (subject: string, message: string) => {
    if (!selectedUser?.email) {
      toast.error("User has no email address");
      setSendingState("error");
      return;
    }
    
    try {
      const accessToken = await getAccessToken();
      
      if (!accessToken) {
        toast.error("Authentication required");
        setSendingState("error");
        return;
      }
      
      console.log("Sending test email with token:", accessToken.substring(0, 10) + "...");
      
      const response = await fetch(`${SUPABASE_URL}/functions/v1/send-test-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          recipient: selectedUser.email,
          subject,
          message
        })
      });
      
      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.error || responseData.details || 'Failed to send test email');
      }
      
      toast.success(`Test email sent to ${selectedUser.email}`);
      setSendingState("success");
      
      setTimeout(() => {
        setIsTestNotificationDialogOpen(false);
        setSendingState("idle");
      }, 1000);
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error(`Failed to send test email: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setSendingState("error");
    }
  };

  const handleSendTestInAppNotification = async (
    type: "reminder" | "system" | "info", 
    title: string, 
    message: string
  ) => {
    try {
      await createTestNotification.mutateAsync({
        userId: selectedUser?.id,
        title,
        message,
        type
      });
      
      setSendingState("success");
      
      setTimeout(() => {
        setIsTestNotificationDialogOpen(false);
        setSendingState("idle");
      }, 1000);
    } catch (error) {
      console.error('Error creating test notification:', error);
      setSendingState("error");
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      await resendInvitation.mutateAsync(invitationId);
    } catch (error) {
      console.error("Error resending invitation:", error);
    }
  };

  const handleDeleteInvitation = async (invitationId: string) => {
    try {
      await deleteInvitation.mutateAsync(invitationId);
    } catch (error) {
      console.error("Error deleting invitation:", error);
    }
  };

  const handleInviteSuccess = () => {
    setActiveTab("invitations");
  };

  const handleOpenDeleteUserDialog = (user: User) => {
    // Block opening the delete dialog if this is the last admin
    if (hasAdminRole(user) && isLastAdminUser(user.id)) {
      toast.error("Cannot delete the last admin user from the system");
      return;
    }
    
    setSelectedUser(user);
    setIsDeleteUserDialogOpen(true);
  };

  const handleDeleteUser = async (userId: string) => {
    // Double-check to prevent deleting the last admin user
    if (isLastAdminUser(userId)) {
      toast.error("Cannot delete the last admin user from the system");
      setIsDeleteUserDialogOpen(false);
      return;
    }
    
    await deleteUser.mutateAsync(userId);
    setIsDeleteUserDialogOpen(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">User Management</h2>
        <div className="flex items-center gap-4">
          <div className="relative w-64">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search users..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button onClick={() => setIsInviteUserDialogOpen(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Invite User
          </Button>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "users" | "invitations")}>
        <TabsList>
          <TabsTrigger value="users" className="flex items-center">
            <Users className="h-4 w-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="invitations" className="flex items-center">
            <UserPlus className="h-4 w-4 mr-2" />
            Invitations {pendingInvitationsCount > 0 && `(${pendingInvitationsCount})`}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="users" className="pt-4">
          <div className="mb-4 flex gap-2">
            <Button
              variant={roleFilter === "USER" ? "default" : "outline"}
              onClick={() => setRoleFilter("USER")}
              className="flex items-center"
            >
              <Users className="h-4 w-4 mr-2" />
              Users
            </Button>
            <Button
              variant={roleFilter === "ADMIN" ? "default" : "outline"}
              onClick={() => setRoleFilter("ADMIN")}
              className="flex items-center"
            >
              <Shield className="h-4 w-4 mr-2" />
              Admins
            </Button>
            <Button
              variant={roleFilter === "TESTER" ? "default" : "outline"}
              onClick={() => setRoleFilter("TESTER")}
              className="flex items-center"
            >
              <Bug className="h-4 w-4 mr-2" />
              Testers
            </Button>
            <Button
              variant={roleFilter === "DEVELOPER" ? "default" : "outline"}
              onClick={() => setRoleFilter("DEVELOPER")}
              className="flex items-center"
            >
              <Shield className="h-4 w-4 mr-2" />
              Developers
            </Button>
            <Button
              variant={roleFilter === "all" ? "default" : "outline"}
              onClick={() => setRoleFilter("all")}
            >
              Show All
            </Button>
          </div>
          
          {isLoadingUsers ? (
            <div className="flex justify-center p-8">
              <p>Loading users...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex justify-center p-8">
              <p>No users found matching the selected criteria</p>
            </div>
          ) : (
            <UsersTable 
              users={filteredUsers}
              onRemoveRole={handleRemoveRole}
              onOpenAddRoleDialog={handleOpenAddRoleDialog}
              onOpenTestNotificationDialog={handleOpenTestNotificationDialog}
              onOpenDeleteUserDialog={handleOpenDeleteUserDialog}
              isLastAdminUser={isLastAdminUser}
            />
          )}
        </TabsContent>
        
        <TabsContent value="invitations" className="pt-4">
          <InvitedUsersTable
            invitedUsers={filteredInvitations}
            isLoading={isLoadingInvitations}
            onResendInvitation={handleResendInvitation}
            onDeleteInvitation={handleDeleteInvitation}
          />
        </TabsContent>
      </Tabs>

      <AddRoleDialog 
        open={isAddRoleDialogOpen && !!selectedUser}
        onOpenChange={setIsAddRoleDialogOpen}
        selectedUser={selectedUser}
        onAddRole={handleAddRole}
        isPending={addRole.isPending}
      />

      <TestNotificationDialog 
        open={isTestNotificationDialogOpen && !!selectedUser}
        onOpenChange={(open) => {
          setIsTestNotificationDialogOpen(open);
          if (!open) setSendingState("idle");
        }}
        selectedUser={selectedUser}
        onSendTestNotification={handleSendTestNotification}
        sendingState={sendingState}
      />

      <DeleteUserDialog
        open={isDeleteUserDialogOpen && !!selectedUser}
        onOpenChange={setIsDeleteUserDialogOpen}
        selectedUser={selectedUser}
        onDeleteUser={handleDeleteUser}
        isPending={deleteUser.isPending}
      />

      <InviteUserDialog
        open={isInviteUserDialogOpen}
        onOpenChange={setIsInviteUserDialogOpen}
        onInviteSuccess={handleInviteSuccess}
      />
    </div>
  );
}
